import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Play, Activity, ShieldCheck, Users, Database } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1631556759511-6ce895fbf0ad?crop=entropy&cs=srgb&fm=webp&w=1200&q=75&auto=format&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwyfHxjbGluaWNhbCUyMHRyaWFsJTIwbWVkaWNhbCUyMHJlc2VhcmNoJTIwbGFiJTIwZG9jdG9yJTIwdGFibGV0fGVufDB8fHx8MTc4ODcyNjAwNHww&ixlib=rb-4.1.0";
const HERO_IMG_SMALL = HERO_IMG.replace("w=1200&q=75", "w=600&q=65");

const lines = [
  { text: "Clinical Trial", accent: false },
  { text: "Software for", accent: false },
  { text: "Connected Clinical", accent: false },
  { text: "Research.", accent: true },
];

const MaskedLine = ({ text, accent, index }) => (
  <span className="mask-line">
    <motion.span
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay: 0.35 + index * 0.15, ease: [0.16, 1, 0.3, 1] }}
      className={accent ? "text-gradient-emerald" : "text-white"}
    >
      {text}
    </motion.span>
  </span>
);

const DashCard = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const rotate = useTransform(scrollYProgress, [0, 1], [0, 6]);

  return (
    <motion.div
      ref={ref}
      style={{ y, rotate }}
      initial={{ opacity: 0, y: 60 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 1.1, delay: 0.9, ease: [0.16, 1, 0.3, 1] }}
      className="relative"
    >
      <div className="absolute -inset-6 bg-emerald-500/10 blur-3xl rounded-full" />
      <div data-keep-dark className="glass-panel relative rounded-2xl overflow-hidden float-slow">
        <div className="relative h-52 sm:h-64 overflow-hidden">
          <img
            src={HERO_IMG}
            srcSet={`${HERO_IMG_SMALL} 600w, ${HERO_IMG} 1200w`}
            sizes="(max-width: 640px) 100vw, 560px"
            alt="Clinical researcher reviewing trial data on a tablet"
            title="Clinical researcher reviewing trial data on a tablet"
            width="1200" height="630" fetchPriority="high" decoding="async"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/40 to-transparent" />
          <div className="scan-line" />
          <div className="absolute top-4 left-4 font-mono-tech text-[10px] tracking-[0.2em] text-emerald-300 bg-slate-950/70 border border-emerald-500/30 rounded-full px-3 py-1">
            ETRIALTRACK • LIVE CONSOLE
          </div>
        </div>
        <div className="p-5 sm:p-6 grid grid-cols-3 gap-3">
          {[
            { icon: Users, label: "ENROLLED", value: "4,812" },
            { icon: Database, label: "EDC RECORDS", value: "98.2%" },
            { icon: ShieldCheck, label: "AUDIT STATE", value: "CLEAN" },
          ].map((s) => (
            <div key={s.label} className="rounded-xl border-2 border-[#1D3461] bg-white p-3 shadow-[4px_4px_0_rgba(29,52,97,0.25)]">
              <s.icon className="w-4 h-4 text-[#2563EB] mb-2" />
              <div className="font-mono-tech text-[9px] tracking-[0.18em] text-[#64748B]">{s.label}</div>
              <div className="font-display text-lg font-bold text-[#12203C] mt-0.5">{s.value}</div>
            </div>
          ))}
        </div>
        <div className="px-5 sm:px-6 pb-6">
          <div className="flex items-center justify-between font-mono-tech text-[10px] tracking-[0.15em] text-slate-500 mb-2">
            <span>SITE ACTIVATION</span>
            <span className="text-emerald-400">87%</span>
          </div>
          <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: "87%" }}
              transition={{ duration: 1.6, delay: 1.6, ease: [0.16, 1, 0.3, 1] }}
              className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-cyan-500"
            />
          </div>
        </div>
      </div>
      <motion.div
        initial={{ opacity: 0, x: 30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.9, delay: 1.5 }}
        className="hidden sm:flex absolute -right-6 top-16 glass-panel rounded-xl px-4 py-3 items-center gap-3"
      >
        <Activity className="w-4 h-4 text-cyan-400" />
        <div>
          <div className="font-mono-tech text-[9px] tracking-[0.18em] text-slate-500">QUERIES RESOLVED</div>
          <div className="font-display text-sm font-bold text-white">+312 this week</div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default function Hero() {
  return (
    <section id="top" className="relative min-h-screen flex items-center pt-28 pb-20 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-[#F7F9FC] via-[#EDF2FA] to-[#F7F9FC]" />
      <div className="hero-grid-lines absolute inset-0" />
      <div className="absolute -top-32 right-0 w-[42rem] h-[42rem] bg-emerald-600/10 blur-[140px] rounded-full" />
      <div className="absolute bottom-0 -left-40 w-[32rem] h-[32rem] bg-cyan-600/10 blur-[140px] rounded-full" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-14 items-center w-full">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="flex items-center gap-3 mb-8"
          >
            <span className="h-px w-10 bg-emerald-500/60" />
            <span className="eyebrow" data-testid="hero-eyebrow">Integrated Clinical Trial Technology</span>
          </motion.div>

          <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[1.05]">
            {lines.map((l, i) => (
              <MaskedLine key={l.text} text={l.text} accent={l.accent} index={i} />
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.05 }}
            className="mt-7 max-w-xl text-base sm:text-lg leading-relaxed text-slate-400"
          >
            Connect data capture, consent, patient-reported outcomes, randomization,
            remote interactions and participant tracking in one operational framework.
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.1 }}
            data-testid="hero-key-takeaway"
            className="mt-5 max-w-xl text-sm leading-relaxed text-slate-500"
          >
            <span className="block font-mono-tech text-[10px] tracking-[0.2em] text-emerald-400 mb-1.5">KEY TAKEAWAY</span>
            In short: eTrialTrack is a unified eClinical platform — sponsors, CROs and research
            sites use it to run EDC, eConsent, ePRO, RTSM/IWRS, TeleHealth and participant
            tracking in one compliant system.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.2 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#demo"
              data-testid="hero-schedule-demo-button"
              className="group flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold rounded-full px-7 py-3.5 btn-glow"
            >
              Request Live Demo
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <a
              href="#platform"
              data-testid="hero-explore-solutions-button"
              className="group flex items-center gap-2 border border-white/15 hover:border-emerald-500/50 text-slate-200 hover:text-white rounded-full px-7 py-3.5 transition-colors duration-300"
            >
              <Play className="w-4 h-4 text-emerald-400" />
              Explore the Platform
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.5 }}
            className="mt-12 flex flex-wrap gap-x-8 gap-y-3 font-mono-tech text-[11px] tracking-[0.18em] text-slate-500"
          >
            <span>99.9% DATA ACCURACY</span>
            <span>120+ GLOBAL STUDIES</span>
            <span>10M+ PATIENT CASES</span>
          </motion.div>
        </div>

        <div className="lg:col-span-5">
          <DashCard />
        </div>
      </div>
    </section>
  );
}
